import { ref, computed, inject, type Ref, type ComputedRef } from 'vue'
import { cloneDeep } from 'lodash-es'
import type { PaginatedMetadataTemplateList, MetadataTemplateField } from '@openapi'
import type { GetInstanceMetadataTemplatesPayload } from '@sdk/payload.types'

export interface UseMetadataTemplatesOptions {
  initialTemplate?: Required<PaginatedMetadataTemplateList>['results'][0]
  itemsRef?: Ref<any[]> | ComputedRef<any[]>
}

export default function useMetadataTemplates(options: UseMetadataTemplatesOptions = {}) {
  const metadataFetcher = inject<
    (params?: GetInstanceMetadataTemplatesPayload) => Promise<PaginatedMetadataTemplateList>
  >('instanceMetadataTemplatesFetcher')

  const template = ref<Required<PaginatedMetadataTemplateList>['results'][0] | undefined>(options.initialTemplate)
  const originalTemplate = ref<Required<PaginatedMetadataTemplateList>['results'][0] | undefined>(
    options.initialTemplate ? cloneDeep(options.initialTemplate) : undefined,
  )
  const isLoading = ref(!options.initialTemplate)

  const getFields = computed<MetadataTemplateField[]>(() => {
    if (!template.value?.sections) return []

    return template.value.sections.flatMap((section) => section.fields)
  })

  function processItemMetadata(item: any): Record<string, any> {
    if (!item?.metadata || !getFields.value.length) return {}

    const result: Record<string, any> = {}

    for (const field of getFields.value) {
      if (
        item.metadata[field.name] !== undefined &&
        item.metadata[field.name] !== null &&
        item.metadata[field.name] !== ''
      ) {
        const value = item.metadata[field.name]

        // Handle boolean values - only show if true, skip if false
        if (typeof value === 'boolean') {
          if (value === true) {
            result[field.name] = {
              value: true,
              label: field.label,
            }
          }
          // Skip false boolean values entirely
          continue
        }

        let displayValue = value

        // For fields with options, use the label of the matching option
        if (field.options && field.options.length) {
          if (Array.isArray(value)) {
            // Handle multi-select options - filter out invalid values
            const validValues = value.filter((val) => field.options?.some((opt) => opt.value === val))
            if (validValues.length === 0) {
              // Skip this field entirely if no valid values
              continue
            }
            const optionLabels = validValues.map((val) => {
              const option = field.options?.find((opt) => opt.value === val)
              return option?.label || val
            })
            displayValue = optionLabels.join(', ')
          } else {
            // Handle single option selection - skip if invalid
            const option = field.options.find((opt) => opt.value === value)
            if (!option) {
              // Skip this field entirely if value is not valid
              continue
            }
            displayValue = option.label || value
          }
        }
        // Handle array values (non-option fields)
        else if (Array.isArray(value)) {
          displayValue = value.join(', ')
        }
        // Handle object values
        else if (typeof value === 'object' && value !== null) {
          try {
            displayValue = JSON.stringify(value)
          } catch (e) {
            displayValue = String(value)
          }
        }

        // Only add to result if displayValue is not empty
        if (displayValue !== '' && displayValue !== null && displayValue !== undefined) {
          result[field.name] = {
            value: displayValue,
            label: field.label,
          }
        }
      }
    }

    return result
  }

  function processCollectionMetadata(items: any[]): Record<string, any> {
    const result: Record<string, any> = {}

    items.forEach((item) => {
      if (item.id) {
        result[item.id] = processItemMetadata(item)
      }
    })

    return result
  }

  // Create processed metadata computed property if itemsRef is provided
  const processedMetadata = options.itemsRef
    ? computed(() => processCollectionMetadata(options.itemsRef!.value))
    : ref({} as Record<string, any>)

  async function fetchTemplate() {
    if (!metadataFetcher) {
      console.warn('[useMetadataTemplates]: No metadataFetcher available')
      return null
    }

    if (template.value) {
      return template.value
    }

    isLoading.value = true

    try {
      const response = await metadataFetcher()
      const results = response?.results ?? []

      if (!results?.length) {
        console.warn('[useMetadataTemplates]: No metadata templates found')
        isLoading.value = false
        return null
      }

      const [templ] = results
      template.value = templ
      originalTemplate.value = cloneDeep(templ)

      return template.value
    } catch (e) {
      console.error('[useMetadataTemplates]: Error fetching metadata templates', e)

      return null
    } finally {
      isLoading.value = false
    }
  }

  return {
    template,
    originalTemplate,
    isLoading,
    getFields,
    processItemMetadata,
    processCollectionMetadata,
    fetchTemplate,
    processedMetadata,
  }
}
